/* Admin calendar — turning API rows into the shape the calendar surfaces read.
 *
 * ClassSession is the wire format; CalSession is what the pulse, the list, the
 * timeline and the drawer all render. Keeping the translation in one place
 * means "what counts as a slot" and "what is this session's resource" are
 * answered once rather than in five components. */

import { timeToMin } from './calendarLayout';
import type { BookableType, CalSession } from './calendarTypes';

/** The subset of ClassSession the calendar needs. Structurally compatible with
 *  SchedulePage's ClassSession and BookingDaySheet's DaySheetSession. */
export interface ApiSession {
  id: string;
  classId: string;
  branchId: string;
  trainerId: string | null;
  roomId: string | null;
  recurringTemplateId: string | null;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  bookedCount: number;
  waitlistCount: number;
  status: string;
  sourceMode?: string;
  class: { id: string; name: string; bookableType?: BookableType | string; category?: string | null; staffRequired?: boolean };
  branch?: { id: string; name: string; displayName: string } | null;
  room?: { id: string; name: string } | null;
  /** Present only when the request asked for `withAttendee`. */
  bookings?: Array<{ member?: { fullName?: string | null; displayName?: string | null } | null }>;
  /** Present only when the request asked for `withCheckins`. */
  checkedInCount?: number;
}

/** Bookable types that expect a staff member on the session. The bookable's own
 *  `staffRequired` flag wins where the API sends it — a gym may run unstaffed
 *  open-mat classes, or a "facility" that a lifeguard has to be on. */
const STAFFED_TYPES: BookableType[] = ['class', 'spa', 'pt', 'consult'];

export const sessionIsStaffed = (s: ApiSession): boolean => {
  const type = (s.class.bookableType || 'class') as BookableType;
  return s.class.staffRequired ?? STAFFED_TYPES.includes(type);
};

/**
 * Is this a pre-generated bookable unit rather than a scheduled session?
 *
 * Slots are what runs are built from: a court that is bookable 06:00–10:00 is
 * four hour-long rows in the database and should read as one bar. A facility
 * grid cell always qualifies; so does a facility slot poured out by a recurring
 * template. Appointments never do — those are somebody's actual booking, and
 * merging two of them would hide a person.
 */
export function isSlotSession(s: ApiSession): boolean {
  const type = (s.class.bookableType || 'class') as BookableType;
  if (s.sourceMode === 'grid') return true;
  return !!s.recurringTemplateId && type === 'facility';
}

/** Now, in the gym's timezone, as a date string plus minutes-of-day. */
export function tzNow(timezone?: string): { dateStr: string; min: number } {
  const now = new Date();
  try {
    if (timezone) {
      const dateFmt = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
      const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false });
      return { dateStr: dateFmt.format(now), min: timeToMin(timeFmt.format(now).slice(0, 5)) };
    }
  } catch { /* an unknown IANA zone falls back to the browser's */ }
  return {
    dateStr: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`,
    min: now.getHours() * 60 + now.getMinutes(),
  };
}

export interface AdaptContext {
  /** trainerId → display name. */
  staffName: (id: string | null) => string | null;
  /** Gym-local now, from tzNow(). */
  now: { dateStr: string; min: number };
  /** Calendar dates of the displayed week, Monday first. Omit outside the week views. */
  weekDates?: string[];
}

/**
 * Flatten one API row.
 *
 * `res` is the resource the session occupies — the room it is booked into, or
 * the staff member when they are the resource (a PT hour is Ravi's hour), or
 * the bookable itself when neither is set. `bk` pairs the bookable with that
 * resource, so two courts running the same "Badminton" bookable never merge
 * into one another's runs.
 */
export function toCalSession(s: ApiSession, ctx: AdaptContext): CalSession {
  const type = (s.class.bookableType || 'class') as BookableType;
  const date = s.date.slice(0, 10);
  const who = ctx.staffName(s.trainerId);
  const res = s.room?.name || (type === 'pt' || type === 'consult' || type === 'spa' ? who : null) || s.class.name;

  const start = timeToMin(s.startTime);
  const end = Math.max(timeToMin(s.endTime), start + 5);

  const past = date < ctx.now.dateStr || (date === ctx.now.dateStr && end <= ctx.now.min);
  const live = date === ctx.now.dateStr && start <= ctx.now.min && end > ctx.now.min;

  const attendee = s.bookings?.[0]?.member;
  const cap = Math.max(0, s.capacity);
  const booked = Math.max(0, s.bookedCount);

  return {
    id: s.id,
    bk: `${s.classId}|${s.roomId || s.trainerId || ''}`,
    name: s.class.name,
    type,
    res,
    who,
    member: cap === 1 && booked > 0 ? (attendee?.displayName || attendee?.fullName || null) : null,
    branchId: s.branchId,
    branch: s.branch?.displayName || s.branch?.name || '',
    date,
    day: ctx.weekDates ? ctx.weekDates.indexOf(date) : -1,
    start,
    end,
    cap,
    booked,
    wait: Math.max(0, s.waitlistCount || 0),
    slot: isSlotSession(s),
    status: s.status === 'cancelled' ? 'cancelled' : 'ok',
    past,
    live,
    sourceMode: s.sourceMode,
    staffed: sessionIsStaffed(s),
    needsStaff: booked > 0 && !s.trainerId && sessionIsStaffed(s),
    checkedIn: Math.max(0, s.checkedInCount ?? 0),
    raw: s,
  };
}

/** Build a trainerId → name lookup from the page's staff list. */
export function staffNamer(
  trainers: Array<{ id: string; name: string | null; displayName: string | null }>
): (id: string | null) => string | null {
  const map = new Map(trainers.map(t => [t.id, t.displayName || t.name || null]));
  return id => (id ? map.get(id) ?? null : null);
}

// ─── Week window ─────────────────────────────────────────────────────────────

/** Opening hours the grids are drawn against, widened to fit anything scheduled
 *  outside them — a 05:30 swim squad must not be clipped off the top. */
export function hourWindow(
  sessions: CalSession[],
  defaults: [number, number] = [6, 22]
): [number, number] {
  let lo = defaults[0] * 60;
  let hi = defaults[1] * 60;
  for (const s of sessions) {
    lo = Math.min(lo, Math.floor(s.start / 60) * 60);
    hi = Math.max(hi, Math.ceil(s.end / 60) * 60);
  }
  return [Math.max(0, Math.floor(lo / 60)), Math.min(24, Math.max(Math.ceil(hi / 60), Math.floor(lo / 60) + 2))];
}
